/**
 * Keeps product card prices in the theme's money format after the Rubik app
 * rewrites them.
 *
 * The theme prints prices through snippets/format-money.liquid, which is
 * deliberately not Shopify's format: dot decimals, no currency code, and a
 * trailing ".00" trimmed off — so "€55". When a colour is picked on a card the
 * app rewrites the same price using Shopify's native money_with_currency_format
 * ("€55,00 EUR"). The longer string reflows the row, which is why the price
 * appears to jump.
 *
 * Rewriting it back keeps the text identical before and after, so nothing
 * moves. This mirrors format-money.liquid step for step; keep the two in sync.
 */
(function () {
  'use strict';

  var CARD_PRICE = '.product-block .product-price';
  var writing = false;

  /**
   * @param {string} text
   * @returns {string} the same amount in the theme's format.
   */
  function toThemeMoney(text) {
    return text
      .replace(/\s*\b[A-Z]{3}\b\s*$/, '') // drop a trailing currency code
      .replace(/(\d)[. \s'](?=\d{3}(?:\D|$))/g, '$1') // drop thousands separators
      .replace(/(\d),(\d{2})(?!\d)/, '$1.$2') // comma decimal becomes a dot
      .replace(/(\d)\.00(?!\d)/, '$1'); // whole amounts lose their .00
  }

  /**
   * Walks text nodes so the markup around the amount — the visually hidden
   * "Regular price" label included — is left untouched.
   * @param {Element} root
   */
  function normalise(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    var node;

    while ((node = walker.nextNode())) {
      if (!/\d/.test(node.nodeValue)) continue;

      var next = toThemeMoney(node.nodeValue);
      if (next !== node.nodeValue) node.nodeValue = next;
    }
  }

  /**
   * @param {Element} price
   */
  function watch(price) {
    if (price.dataset.priceFormatBound) return;
    price.dataset.priceFormatBound = 'true';

    normalise(price);

    new MutationObserver(function () {
      if (writing) return;
      writing = true;
      normalise(price);
      writing = false;
    }).observe(price, { childList: true, subtree: true, characterData: true });
  }

  function scan(scope) {
    (scope || document).querySelectorAll(CARD_PRICE).forEach(watch);
  }

  function init() {
    scan(document);

    // Cards arrive later too: infinite scroll, filtering, quick buy.
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches(CARD_PRICE)) watch(node);
          if (node.querySelectorAll) scan(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
